import { Component, computed, inject, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Observable, of } from 'rxjs';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { LutService } from '../../../../../services/lut.service';
import { CompanySocietyImpactService } from '../../../../../services/companies/company-society-impact.service';
import { CompanySocietyImpactDto, UpsertSocietyImpactRequest } from '../../../../../models/api/companySocietyImpactDto';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { CsrStandard } from '../../../../../models/csrStandard';
import { CsrAssuranceProvider } from '../../../../../models/csrAssuranceProvider';
import { Currency } from '../../../../../models/currency';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomDateAdapter, DD_MM_YYYY_FORMAT } from '../../../../directors/director-form/director-form.component';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { TextNormalizeDirective } from '../../../../../directives/text-normalize.directive';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-society-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    TextNormalizeDirective,
    ReadOnlyTabDirective
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMAT }
  ],
  templateUrl: './society-tab.component.html',
  styleUrl: './society-tab.component.scss'
})
export class SocietyTabComponent implements OnInit, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private lutService = inject(LutService);
  private societyImpactService = inject(CompanySocietyImpactService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Society));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  isCarriedForward = false;
  showKeywords = true;
  otherMeasurement: string = '';
  otherData: CompanySocietyImpactDto | null = null;

  environmentalStandards: CsrStandard[] = [];
  reportingStandards: CsrStandard[] = [];
  allStandards: CsrStandard[] = [];
  assuranceProviders: CsrAssuranceProvider[] = [];
  currencies: Currency[] = [];

  form: FormGroup = this.fb.group({
    chiefSustainabilityOfficer: [''],
    isCsrCommittee: [false],
    environmentalStandardId: [''],
    standardId: [''],
    assuranceProviderId: [''],
    assuranceName: [''],
    assuranceDate: [null as Date | null],
    reportingStandard1Id: [''],
    reportingStandard2Id: [''],
    cashDonations: [null as number | null],
    cashDonationsCurrencyId: [''],
    inKindDonations: [null as number | null],
    inKindDonationsCurrencyId: [''],
    employeeDonations: [null as number | null],
    employeeDonationsCurrencyId: [''],
    politicalContributions: [null as number | null],
    politicalContributionsCurrencyId: [''],
    communityLendingInvestment: [null as number | null],
    communityLendingInvestmentCurrencyId: [''],
    charitiesSupported: [null as number | null],
    charitiesSupportedCurrencyId: [''],
    cashDonationsPercentage: [null as number | null],
    inKindDonationsPercentage: [null as number | null],
    employeeDonationsPercentage: [null as number | null],
    politicalContributionsPercentage: [null as number | null],
    communityLendingInvestmentPercentage: [null as number | null],
    charitiesSupportedPercentage: [null as number | null],
    donationsTotal: [null as number | null],
    donationsTotalCurrencyId: [''],
    volunteerHours: [null as number | null],
    volunteerEmployees: [null as number | null],
    antiCorruptionTrainingBoardOfDirectors: [null as number | null],
    antiCorruptionTrainingEmployees: [null as number | null],
    antiCorruptionTrainingSuppliers: [null as number | null],
    confirmedIncidentsOfCorruption: [null as number | null],
    discriminationAndHarassmentIncidents: [null as number | null],
    lossesOfCustomerData: [null as number | null],
    monetaryLosses: [null as number | null],
    monetaryLossesCurrencyId: [''],
    hasHumanRightsPolicy: [false],
    humanRightsTrainingHours: [null as number | null],
    humanRightsParticipants: [null as number | null],
    humanRightsParticipantsPersons: [null as number | null],
    totalNumberOfSuppliers: [null as number | null],
    paymentToSuppliers: [null as number | null],
    paymentToSuppliersCurrencyId: [''],
    comments: [''],
    keywords: this.fb.group({
      assurance: [''],
      donation: [''],
      corruption: [''],
      discrimination: [''],
      humanRight: [''],
      suppliers: ['']
    })
  });

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  /**
   * An Other Measurement figure, written the way NumberFormatDirective writes the input it sits
   * under: `decimals` places when the input fixes them, the locale's own (up to three) otherwise.
   * Rounding to whole numbers hid the other year's percentages and hours behind integers.
   */
  formatNumber(value: number, decimals: number | null = null): string {
    const opts: Intl.NumberFormatOptions = decimals != null
      ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
      : {};
    return value.toLocaleString('de-DE', opts);
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB');
  }

  private readonly donationFields = [
    'cashDonations', 'inKindDonations', 'employeeDonations',
    'politicalContributions', 'communityLendingInvestment', 'charitiesSupported'
  ];

  donationsTotalAutoFilled = false;

  private updatingFromSync = false;

  private setupDonationPercentageSync(): void {
    for (const field of this.donationFields) {
      this.form.get(field)!.valueChanges.subscribe(() => {
        if (this.updatingFromSync) return;
        this.updatingFromSync = true;
        this.recalcTotalAndPercentages();
        this.updatingFromSync = false;
      });

      this.form.get(field + 'Percentage')!.valueChanges.subscribe(pct => {
        if (this.updatingFromSync) return;
        this.updatingFromSync = true;
        const total = this.getDonationsSum();
        if (total) {
          const amount = pct != null ? (pct / 100) * total : null;
          const ctrl = this.form.get(field)!;
          ctrl.setValue(amount != null ? Math.round(amount) : null, { emitEvent: false });
          ctrl.markAsDirty();
          this.recalcTotalAndPercentages();
        }
        this.updatingFromSync = false;
      });
    }

    this.form.get('donationsTotal')!.valueChanges.subscribe(total => {
      if (this.updatingFromSync) return;
      this.updatingFromSync = true;
      this.recalcPercentages(total);
      this.updatingFromSync = false;
    });
  }

  private getDonationsSum(): number {
    let sum = 0;
    for (const field of this.donationFields) {
      sum += this.form.get(field)?.value ?? 0;
    }
    return sum;
  }

  private recalcPercentages(total: number | null): void {
    for (const field of this.donationFields) {
      const value = this.form.get(field)?.value;
      const pct = (total && value != null) ? Math.round((value / total) * 100 * 100) / 100 : null;
      this.form.get(field + 'Percentage')!.setValue(pct, { emitEvent: false });
    }
  }

  private recalcTotalAndPercentages(flash: boolean = true): void {
    const total = this.getDonationsSum();
    const totalCtrl = this.form.get('donationsTotal')!;
    const newValue = total || null;
    const valueChanged = totalCtrl.value !== newValue;
    totalCtrl.setValue(newValue, { emitEvent: false });
    totalCtrl.markAsDirty();
    this.recalcPercentages(total);
    if (flash && valueChanged && newValue != null) {
      this.donationsTotalAutoFilled = true;
      setTimeout(() => this.donationsTotalAutoFilled = false, 2000);
    }
  }

  getOtherCurrencyName(currencyId: string | null | undefined): string {
    if (!currencyId) return '';
    return this.currencies.find(c => c.id === currencyId)?.name ?? '';
  }

  getOtherStandardName(standardId: string | null | undefined): string {
    if (!standardId) return '';
    return this.allStandards.find(s => s.id === standardId)?.name ?? '';
  }

  getOtherAssuranceProviderName(): string {
    if (!this.otherData?.assuranceProviderId) return '';
    return this.assuranceProviders.find(p => p.id === this.otherData!.assuranceProviderId)?.name ?? '';
  }

  private readonly keywordMapping: { formField: string; type: KeywordType }[] = [
    { formField: 'assurance', type: KeywordType.SocietyAssurance },
    { formField: 'donation', type: KeywordType.SocietyDonation },
    { formField: 'corruption', type: KeywordType.SocietyCorruption },
    { formField: 'discrimination', type: KeywordType.SocietyDiscrimination },
    { formField: 'humanRight', type: KeywordType.SocietyHumanRight },
    { formField: 'suppliers', type: KeywordType.SocietySuppliers },
  ];

  keywordFields: { formField: string; label: string }[] = [
    { formField: 'assurance', label: 'Assurance' },
    { formField: 'donation', label: 'Donation' },
    { formField: 'corruption', label: 'Corruption' },
    { formField: 'discrimination', label: 'Discrimination' },
    { formField: 'humanRight', label: 'Human Rights' },
    { formField: 'suppliers', label: 'Suppliers' },
  ];

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('society'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'society', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'society', year: this.selectedYear }));
    }

    this.patchKeywords();
    this.setupDonationPercentageSync();
    this.loadLookupData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  private patchKeywords(): void {
    const patch: Record<string, string> = {};
    for (const mapping of this.keywordMapping) {
      const kw = this.keywords.find(k => k.type === mapping.type);
      patch[mapping.formField] = kw?.value ?? '';
    }
    this.form.get('keywords')!.patchValue(patch, { emitEvent: false });
    this.form.markAsPristine();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.loadOtherMeasurementData();
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'society', year }));
    this.loadData();
  }

  private loadOtherMeasurementData(): void {
    if (!this.companyId || !this.otherMeasurement) return;
    this.societyImpactService.get(this.companyId, parseInt(this.otherMeasurement)).subscribe({
      next: resource => {
        this.otherData = resource.state === 'present' ? resource.data : null;
      },
      error: () => {
        this.otherData = null;
      }
    });
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.form.get('keywords')!.dirty;
    const hasSocietyChanges = this.form.dirty && !hasKeywordChanges;

    if (hasSocietyChanges && hasKeywordChanges) return 'society impact data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'society impact data';
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  private performSave(): void {
    if (!this.companyId) return;

    const v = this.form.value;
    const toUtcDate = (value: Date | string | null): string | null => {
      if (!value) return null;
      const date = value instanceof Date ? value : new Date(value);
      return isNaN(date.getTime()) ? null : date.toISOString();
    };

    // Build keyword upsert requests
    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, this.keywordMapping, this.keywords);

    const hasSocietyData = this.isCarriedForward || Object.keys(v).some(k => k !== 'keywords' && this.form.get(k)?.dirty);
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => this.keywordMapping.some(m => m.type === k.type)
    );

    if (!hasSocietyData && !hasKeywordData) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();
    const request: UpsertSocietyImpactRequest = {
      year: parseInt(this.selectedYear),
      chiefSustainabilityOfficer: v.chiefSustainabilityOfficer || null,
      isCsrCommittee: v.isCsrCommittee,
      environmentalStandardId: v.environmentalStandardId || null,
      standardId: v.standardId || null,
      assuranceProviderId: v.assuranceProviderId || null,
      assuranceName: v.assuranceName || null,
      assuranceDate: toUtcDate(v.assuranceDate),
      reportingStandard1Id: v.reportingStandard1Id || null,
      reportingStandard2Id: v.reportingStandard2Id || null,
      cashDonations: v.cashDonations ?? null,
      cashDonationsCurrencyId: v.cashDonationsCurrencyId || null,
      inKindDonations: v.inKindDonations ?? null,
      inKindDonationsCurrencyId: v.inKindDonationsCurrencyId || null,
      employeeDonations: v.employeeDonations ?? null,
      employeeDonationsCurrencyId: v.employeeDonationsCurrencyId || null,
      politicalContributions: v.politicalContributions ?? null,
      politicalContributionsCurrencyId: v.politicalContributionsCurrencyId || null,
      communityLendingInvestment: v.communityLendingInvestment ?? null,
      communityLendingInvestmentCurrencyId: v.communityLendingInvestmentCurrencyId || null,
      charitiesSupported: v.charitiesSupported ?? null,
      charitiesSupportedCurrencyId: v.charitiesSupportedCurrencyId || null,
      donationsTotal: v.donationsTotal ?? null,
      donationsTotalCurrencyId: v.donationsTotalCurrencyId || null,
      volunteerHours: v.volunteerHours ?? null,
      volunteerEmployees: v.volunteerEmployees ?? null,
      antiCorruptionTrainingBoardOfDirectors: v.antiCorruptionTrainingBoardOfDirectors ?? null,
      antiCorruptionTrainingEmployees: v.antiCorruptionTrainingEmployees ?? null,
      antiCorruptionTrainingSuppliers: v.antiCorruptionTrainingSuppliers ?? null,
      confirmedIncidentsOfCorruption: v.confirmedIncidentsOfCorruption ?? null,
      discriminationAndHarassmentIncidents: v.discriminationAndHarassmentIncidents ?? null,
      lossesOfCustomerData: v.lossesOfCustomerData ?? null,
      monetaryLosses: v.monetaryLosses ?? null,
      monetaryLossesCurrencyId: v.monetaryLossesCurrencyId || null,
      hasHumanRightsPolicy: v.hasHumanRightsPolicy,
      humanRightsTrainingHours: v.humanRightsTrainingHours ?? null,
      humanRightsParticipants: v.humanRightsParticipants ?? null,
      humanRightsParticipantsPersons: v.humanRightsParticipantsPersons ?? null,
      totalNumberOfSuppliers: v.totalNumberOfSuppliers ?? null,
      paymentToSuppliers: v.paymentToSuppliers ?? null,
      paymentToSuppliersCurrencyId: v.paymentToSuppliersCurrencyId || null,
      comments: v.comments || null
    };

    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasSocietyData
      ? this.societyImpactService.upsert(this.companyId!, request)
      : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([_societyResult, keywordsResult]) => {
        // The year has a record of its own now. Left set, the flag kept Save enabled after the
        // save went through, until the tab was reloaded.
        this.isCarriedForward = false;
        this.showYearWarning = false;
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private loadLookupData(): void {
    this.spinnerService.show();

    forkJoin([
      this.lutService.getCsrStandards(),
      this.lutService.getCsrAssuranceProviders(),
      this.lutService.getCurrencies()
    ]).subscribe({
      next: ([standards, providers, currencies]) => {
        const sorted = standards.sort((a, b) => a.name.localeCompare(b.name));
        this.environmentalStandards = sorted.filter(s => s.type === 2);
        this.reportingStandards = sorted.filter(s => s.type === 1);
        this.allStandards = sorted;
        this.assuranceProviders = providers.sort((a, b) => a.name.localeCompare(b.name));
        this.currencies = currencies.sort((a, b) => a.name.localeCompare(b.name));
        this.loadData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load lookup data');
        console.error('Lookup data error:', err);
      }
    });
  }

  private loadData(): void {
    if (!this.companyId) {
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.show();
    this.societyImpactService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const formValues = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.isCarriedForward = resource.state === 'carried-forward';
        this.showYearWarning = this.isCarriedForward || resource.state === 'absent';
        if (formValues) {
          this.patchForm(formValues);
        } else {
          // Nothing saved for the year: the amounts are still quoted in the company's
          // country currency, which the API hands over beside the empty envelope.
          this.resetForm(resource.context?.defaultCurrencyId ?? '');
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        this.isCarriedForward = false;
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading society impact data');
        }

        console.error('Society impact error:', error);
      }
    });
  }

  private patchForm(data: CompanySocietyImpactDto): void {
    this.form.patchValue({
      chiefSustainabilityOfficer: data.chiefSustainabilityOfficer ?? '',
      isCsrCommittee: data.isCsrCommittee ?? false,
      environmentalStandardId: data.environmentalStandardId ?? '',
      standardId: data.standardId ?? '',
      assuranceProviderId: data.assuranceProviderId ?? '',
      assuranceName: data.assuranceName ?? '',
      assuranceDate: data.assuranceDate ? new Date(data.assuranceDate) : null,
      reportingStandard1Id: data.reportingStandard1Id ?? '',
      reportingStandard2Id: data.reportingStandard2Id ?? '',
      cashDonations: data.cashDonations,
      cashDonationsCurrencyId: data.cashDonationsCurrencyId ?? '',
      inKindDonations: data.inKindDonations,
      inKindDonationsCurrencyId: data.inKindDonationsCurrencyId ?? '',
      employeeDonations: data.employeeDonations,
      employeeDonationsCurrencyId: data.employeeDonationsCurrencyId ?? '',
      politicalContributions: data.politicalContributions,
      politicalContributionsCurrencyId: data.politicalContributionsCurrencyId ?? '',
      communityLendingInvestment: data.communityLendingInvestment,
      communityLendingInvestmentCurrencyId: data.communityLendingInvestmentCurrencyId ?? '',
      charitiesSupported: data.charitiesSupported,
      charitiesSupportedCurrencyId: data.charitiesSupportedCurrencyId ?? '',
      donationsTotal: data.donationsTotal,
      donationsTotalCurrencyId: data.donationsTotalCurrencyId ?? '',
      volunteerHours: data.volunteerHours,
      volunteerEmployees: data.volunteerEmployees,
      antiCorruptionTrainingBoardOfDirectors: data.antiCorruptionTrainingBoardOfDirectors,
      antiCorruptionTrainingEmployees: data.antiCorruptionTrainingEmployees,
      antiCorruptionTrainingSuppliers: data.antiCorruptionTrainingSuppliers,
      confirmedIncidentsOfCorruption: data.confirmedIncidentsOfCorruption,
      discriminationAndHarassmentIncidents: data.discriminationAndHarassmentIncidents,
      lossesOfCustomerData: data.lossesOfCustomerData,
      monetaryLosses: data.monetaryLosses,
      monetaryLossesCurrencyId: data.monetaryLossesCurrencyId ?? '',
      hasHumanRightsPolicy: data.hasHumanRightsPolicy ?? false,
      humanRightsTrainingHours: data.humanRightsTrainingHours,
      humanRightsParticipants: data.humanRightsParticipants,
      humanRightsParticipantsPersons: data.humanRightsParticipantsPersons,
      totalNumberOfSuppliers: data.totalNumberOfSuppliers,
      paymentToSuppliers: data.paymentToSuppliers,
      paymentToSuppliersCurrencyId: data.paymentToSuppliersCurrencyId ?? '',
      comments: data.comments ?? ''
    }, { emitEvent: false });

    if (data.donationsTotal != null) {
      this.recalcPercentages(data.donationsTotal);
    } else {
      this.recalcTotalAndPercentages(false);
    }
    this.form.markAsPristine();
  }

  /**
   * @param currencyId The currency every amount defaults to — the company's country currency,
   * or '' when there is none to fall back to.
   */
  private resetForm(currencyId: string = ''): void {
    const keywordsValue = this.form.get('keywords')!.value;
    this.form.reset({
      chiefSustainabilityOfficer: '',
      isCsrCommittee: false,
      environmentalStandardId: '',
      standardId: '',
      assuranceProviderId: '',
      assuranceName: '',
      assuranceDate: null,
      reportingStandard1Id: '',
      reportingStandard2Id: '',
      cashDonations: null,
      cashDonationsCurrencyId: currencyId,
      inKindDonations: null,
      inKindDonationsCurrencyId: currencyId,
      employeeDonations: null,
      employeeDonationsCurrencyId: currencyId,
      politicalContributions: null,
      politicalContributionsCurrencyId: currencyId,
      communityLendingInvestment: null,
      communityLendingInvestmentCurrencyId: currencyId,
      charitiesSupported: null,
      charitiesSupportedCurrencyId: currencyId,
      cashDonationsPercentage: null,
      inKindDonationsPercentage: null,
      employeeDonationsPercentage: null,
      politicalContributionsPercentage: null,
      communityLendingInvestmentPercentage: null,
      charitiesSupportedPercentage: null,
      donationsTotal: null,
      donationsTotalCurrencyId: currencyId,
      volunteerHours: null,
      volunteerEmployees: null,
      antiCorruptionTrainingBoardOfDirectors: null,
      antiCorruptionTrainingEmployees: null,
      antiCorruptionTrainingSuppliers: null,
      confirmedIncidentsOfCorruption: null,
      discriminationAndHarassmentIncidents: null,
      lossesOfCustomerData: null,
      monetaryLosses: null,
      monetaryLossesCurrencyId: currencyId,
      hasHumanRightsPolicy: false,
      humanRightsTrainingHours: null,
      humanRightsParticipants: null,
      humanRightsParticipantsPersons: null,
      totalNumberOfSuppliers: null,
      paymentToSuppliers: null,
      paymentToSuppliersCurrencyId: currencyId,
      comments: ''
    }, { emitEvent: false });
    this.form.get('keywords')!.patchValue(keywordsValue, { emitEvent: false });
    this.form.markAsPristine();
  }
}
